import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';
import SlowMotionVideoRoundedIcon from '@mui/icons-material/SlowMotionVideoRounded';
import { Button, Typography, Box } from '@mui/material';
import { TimerOutlined, CachedRounded, AddRounded } from '@mui/icons-material';
import Tooltip from '@src/components/Tooltip';
import ResizeTextBox from '@src/components/ResizeTextBox';
import ProcessHandler from '@src/components/ProcessHandler';
import RequestStatus from '@src/components/RequestStatus';
import CrownImg from '@src/assets/images/crown.png';
import { delay } from '@src/utils/delay';
import { splitParagraphIntoSentence } from '@src/services/sentence';
import {
  BREAK_TIME_REGEX,
  REGEX,
  REQUEST_STATUS,
  VOICE_PROVIDER,
} from '@src/constants/voice';
import ROUTES from '@src/constants/route';
import CircularProgress from '@mui/material/CircularProgress';
import actions from '@src/redux/actions';
import { TTS_GUIDE } from '@src/constants/tourGuide';
import { MAX_LENGTH_TITLE } from '@src/constants/request';
import {
  TIME_BETWEEN_GENERATION_CLICKS,
  LOCAL_STORAGE_EXPIRES_TIME,
  SET_TIME_AUTO_SAVE,
  SILENCE_AUDIO_URL,
} from '@src/constants/tts';
import {
  LANGUAGE,
  PREDICT_SECONDS_PER_CHARACTER,
  SCREEN_TYPE,
} from '@src/constants';
import { SUB_STATUS, VOICE_CLONE_TYPE } from '@src/constants/voiceCloning';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  setDataLocalStorage,
  getDataLocalStorage,
  deleteDataLocalStorage,
} from '@src/utils/localStorage';
import useRequestAnimationStatus from '@src/hooks/useRequestAnimationStatus';
import { convertParagraphsToText } from '@src/services/paragraph';
import debounce from '@src/utils/debounce';
import { formatAudioTime } from '@src/utils/time';
import { getTTSTourGuideValue } from '@src/utils/tourGuide';
import { calculateElapsedDateTime } from '@src/utils/date';
import { isTerminalRequestStatus } from '@src/utils/requestStatus';
import useElementWidth from '@src/hooks/useElementWidth';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import { MAX_TITLE_LENGTH } from '@src/configs';
import { isEmpty, isEqual } from 'lodash';
import i18n from '@src/languages';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useUserInfo from '@src/hooks/useTtsUser';
import { useCanUseEOLVoice } from '@src/hooks/useCanUseEOLVoice';
import useCustomerSupportView from '@src/hooks/useCustomerSupportView';
import StatusSaving from './StatusSaving';
import ActionButtons from './ActionButton';
import ActionButtonsNew from './ActionButtonNew';

import {
  StyledTitleBar,
  StyledCreateNewEditor,
  StyledPopover,
  StyledButtonPlay,
} from './index.style';
import PlayAudioButton from '../ActionButtonNew/PlayAudioButton';

const SAVE_TITLE_WIDTH = 100;
const TITLE_MARGIN_RIGHT = 20;

const TitleBar = ({
  isNewLayout = 0,
  loading,
  showWarningFactorCharcterDialog,
  onCreateSynthesis,
  characterExceed,
  inputLength = 0,
  isLoadDraft,
  onLoadEditContent,
  onChangeInputLength,
  onOpenConfirmDialog,
  content,
  enableEditor,
  onCreateNewEditor,
  audioRef,
  onChangeRequestLoading,
  onEnableEditor,
  triggerBtn,
  setCloseMoreMenu,
  showNonVbeeWarning,
  onCancelTriggerBtn,
  showWarningCannotUseVoiceDialog,
}) => {
  const { isCustomerSupportView } = useCustomerSupportView();
  const {
    relateDataRequest,
    synthesisRequest,
    openSentences,
    voice,
    advanceFeature,
    sentences,
    paragraphs,
    defaultSample,
  } = useSelector((state) => state.synthesisRequest);
  const history = useHistory();
  const { audioLink: audioPlayerLink } = useSelector(
    (state) => state.audioPlayer,
  );
  const { ttsUser } = useUserInfo();
  const user = useSelector((state) => state.auth.user);
  const hasConvertTts = !loading;
  const totalCharacters = useMemo(
    () => ttsUser?.maxLengthInputText || 0,
    [ttsUser],
  );
  const estimateAudioLength = formatAudioTime(
    inputLength * PREDICT_SECONDS_PER_CHARACTER,
  );
  const [groupLoading, setGroupLoading] = useState(false);
  const [openTooltipCostsCredit, setOpenTooltipCostsCredit] = useState(false);
  const requestInfoRef = useRef();
  const requestInfoWidth = useElementWidth(requestInfoRef);
  const titleRef = useRef();
  const titleWidth = useElementWidth(titleRef);
  const { getFeatureValue } = useFeatureFlags();
  const { canUseEOLVoice } = useCanUseEOLVoice();
  const userInFeatureVoiceCloning = getFeatureValue(
    FEATURE_KEYS.VOICE_CLONING,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const canUserUseVoiceCloning = validateUserPackageCanUseVoiceCloning({
    ttsUser,
    isUserInWhiteList: userInFeatureVoiceCloning,
    voice,
  });

  const splitParagraphEnabled = getFeatureValue(FEATURE_KEYS.SPLIT_PARAGRAPH, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const enableCreditUsagePreview = getFeatureValue(
    FEATURE_KEYS.ENABLE_CREDIT_USAGE_PREVIEW,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const isSynthesisRequestWithVoiceCloning =
    voice?.provider &&
    voice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING &&
    voice?.userId === user?.id &&
    voice?.status === SUB_STATUS.PRIVATE;

  const eolVoice = voice?.eolDate;

  const routerPath = useLocation();
  const [isOpenDialog, setIsOpenDialog] = useState(true);
  const { t } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();

  const [isShowUp, setIsShowUp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isClickTransfer, setIsClickTransfer] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [mobileCreateButtonAnchorEl, setMobileCreateButtonAnchorEl] =
    useState(null);
  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const isExpiredPackage = checkExpiredDate(user.packageExpiryDate);
  const handleOpenMobileCreateButton = (e) =>
    setMobileCreateButtonAnchorEl(e.target);
  const handleCloseMobileCreateButton = () =>
    setMobileCreateButtonAnchorEl(null);

  const handleChangeShowUp = () => {
    if (!isSaving) setIsShowUp(false);
  };

  const handleCreateNewEditor = (isNew = true) => {
    // true when refresh all and create new,false when clone data
    onCreateNewEditor(isNew);
    handleCloseMobileCreateButton();
  };

  const handleValidateTTSTitle = (value) => {
    if (value.length <= MAX_LENGTH_TITLE) return true;
    dispatch(
      actions.noti.push({
        severity: 'warning',
        message: 'updateTitleMaxLength',
        value: MAX_LENGTH_TITLE,
      }),
    );
    return false;
  };

  const handleChangePlayAudio = (audioLink) => {
    if (audioPlayerLink === audioLink) {
      dispatch(actions.audioPlayer.updateStatus(true));
      dispatch(actions.audioPlayer.updateShowAudioPlayer(true));
    } else {
      // eslint-disable-next-line no-param-reassign
      audioRef.current.src = SILENCE_AUDIO_URL;
      audioRef.current.load();
      audioRef.current.play();
      dispatch(actions.audioPlayer.updateAudioLink(audioLink));
      dispatch(actions.audioPlayer.updateStatus(true));
      dispatch(actions.audioPlayer.updateMetaData({ currentTime: 0 }));
      dispatch(actions.audioPlayer.updateShowAudioPlayer(true));
    }
  };

  const handleCheckIsSampleScript = () => {
    const { text: sampleText, audios } = defaultSample;
    const defaultSampleData = audios?.[0] || {};
    const {
      audioType: sampleAudioType,
      bitrate: sampleBitrate,
      sampleRate: sampleSampleRate,
      voiceCode: sampleVoiceCode,
      audioLink: sampleAudioLink,
      speed: sampleSpeed,
    } = defaultSampleData;

    const { audioType, bitrate, sampleRate, text, speed } = synthesisRequest;

    const isDifferent = (value, sampleValue) =>
      value !== undefined && value !== sampleValue;

    if (
      isDifferent(text, sampleText) ||
      isDifferent(audioType, sampleAudioType) ||
      isDifferent(bitrate, sampleBitrate) ||
      isDifferent(sampleRate, sampleSampleRate) ||
      isDifferent(voice?.code, sampleVoiceCode) ||
      isDifferent(speed, sampleSpeed)
    )
      return;

    const dataSynthesis = {
      text: sampleText,
      title: synthesisRequest.title,
      audioLink: sampleAudioLink,
      voice,
    };

    handleChangePlayAudio(sampleAudioLink);
    dispatch(actions.synthesisRequest.updateSynthesisConfig(dataSynthesis));
    dispatch(actions.synthesisRequest.updateLoadingSynthesisConfig(true));
    dispatch(actions.audioPlayer.updatePreviewRequestInfo(dataSynthesis));
    dispatch(actions.synthesisRequest.updateShowSampleScripts(false));
    onChangeInputLength(sampleText.length);
    setIsDefault(false);
  };

  const checkDefaultState = () => {
    const { text: sampleText, audios } = defaultSample;
    const defaultSampleData = audios?.[0] || {};
    const {
      audioType: sampleAudioType,
      bitrate: sampleBitrate,
      sampleRate: sampleSampleRate,
      voiceCode: sampleVoiceCode,
      speed: sampleSpeed,
    } = defaultSampleData;

    const { audioType, bitrate, sampleRate, text, speed } = synthesisRequest;

    const isDifferent = (value, sampleValue) =>
      value !== undefined && value !== sampleValue;

    if (
      inputLength !== 0 ||
      isDifferent(text, sampleText) ||
      isDifferent(audioType, sampleAudioType) ||
      isDifferent(bitrate, sampleBitrate) ||
      isDifferent(sampleRate, sampleSampleRate) ||
      isDifferent(voice?.code, sampleVoiceCode) ||
      isDifferent(speed, sampleSpeed)
    ) {
      setIsDefault(false);
    } else {
      if (defaultSample.text) return;
      setIsDefault(true);
    }
  };
  useEffect(() => {
    if (defaultSample.text) {
      checkDefaultState();
    }
  }, [inputLength, defaultSample, synthesisRequest, voice]);

  const updateRequestStatus = () => {
    if (!enableEditor) {
      onEnableEditor(true);
      dispatch(actions.synthesisRequest.cloneSynthesisRequest());
    }
  };

  const handleChangeTTSTitle = async ({ newTitle }) => {
    if (!synthesisRequest.id) {
      dispatch(
        actions.synthesisRequest.updateSynthesisRequest('title', newTitle),
      );
      return;
    }
    try {
      if (newTitle === synthesisRequest.title) return;

      if (newTitle.length > MAX_LENGTH_TITLE) {
        dispatch(
          actions.noti.push({
            severity: 'warning',
            message: 'updateTitleMaxLength',
            value: MAX_LENGTH_TITLE,
          }),
        );
        return;
      }
      // await apis.requests.updateRequest(synthesisRequest.id, {
      //   title: newTitle,
      // });
      if (!enableEditor) updateRequestStatus();
      dispatch(
        actions.synthesisRequest.updateSynthesisRequest('title', newTitle),
      );
      // dispatch(
      //   actions.noti.push({
      //     severity: 'success',
      //     message: 'updateSuccess',
      //   }),
      // );
      onChangeRequestLoading(true);
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: error?.response?.data?.error_message || 'updateFailure',
        }),
      );
    }
  };

  const handleShowDialogUpgradePackage = () => {
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('voiceCloningUpgrade'),
        img: CrownImg,
        classNameImg: 'custom-img',
        onUpgrade: () => {
          history.push(ROUTES.PAYMENT);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );

    return null;
  };

  const handleShowDialogUpgradeInstantVoiceCloning = () => {
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('instantVoiceCloningUpgrade'),
        img: CrownImg,
        classNameImg: 'custom-img',
        showButtonUnderstand: true,
        onUpgrade: () => {
          history.push(ROUTES.PAYMENT);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
        maxWidth: 'sm',
      }),
    );

    return null;
  };

  const handleClickTransfer = async () => {
    if (openSentences && !splitParagraphEnabled) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: t('splitParagraphNotSupported'),
        }),
      );
      return;
    }

    if (eolVoice && !openSentences && !canUseEOLVoice({ voice, user })) {
      showWarningCannotUseVoiceDialog();
      return;
    }

    if (!canUserUseVoiceCloning && isSynthesisRequestWithVoiceCloning) {
      if (voice?.type === VOICE_CLONE_TYPE.ZERO_SHOT)
        handleShowDialogUpgradeInstantVoiceCloning();
      else handleShowDialogUpgradePackage();
      return;
    }
    if (showNonVbeeWarning && !triggerBtn) {
      showWarningFactorCharcterDialog();
      return;
    }
    if (!isDefault && (!hasConvertTts || isClickTransfer)) {
      return;
    }
    const isDefaultRequest = isDefault;
    if (!isClickTransfer) {
      setIsClickTransfer(true);
      const startTime = Date.now();
      if (!isDefaultRequest) {
        await onCreateSynthesis(isDefaultRequest);
      }
      const endTime = Date.now();
      const durationInSeconds = endTime - startTime;
      if (
        durationInSeconds < TIME_BETWEEN_GENERATION_CLICKS &&
        !isDefaultRequest
      ) {
        await delay(TIME_BETWEEN_GENERATION_CLICKS - durationInSeconds);
      }
      setIsClickTransfer(false);
      if (isDefaultRequest) handleCheckIsSampleScript();
    }
  };

  useEffect(() => {
    if (triggerBtn) {
      handleClickTransfer();
      onCancelTriggerBtn();
    }
  }, [triggerBtn]);

  const handleSaveDraftOld = () => {
    if (isOpenDialog && !inputLength) return;
    let sentencesSave;
    if (advanceFeature) sentencesSave = paragraphs;

    if (!advanceFeature) {
      if (openSentences) {
        sentencesSave = sentences;
      } else {
        sentencesSave = splitParagraphIntoSentence(
          content.getCurrentContent().getPlainText('\n'),
          voice,
        );
      }
    }
    const dataDraft = {
      advanceFeature,
      openSentences,
      inputLength,
      synthesisRequest,
      sentencesSave,
      voice,
      enableEditor,
    };
    setDataLocalStorage('dataDraft', dataDraft, LOCAL_STORAGE_EXPIRES_TIME);
    if (
      !isEmpty(relateDataRequest) &&
      !isEqual(relateDataRequest, getDataLocalStorage('relatedRequest') || {})
    ) {
      setDataLocalStorage(
        'relatedRequest',
        relateDataRequest,
        LOCAL_STORAGE_EXPIRES_TIME,
      );
    }

    setIsSaving(false);
  };

  const processContentDraft = () => {
    if (inputLength) handleSaveDraftOld();

    if (!isOpenDialog && !inputLength) {
      setIsSaving(false);
      deleteDataLocalStorage('dataDraft');
    }
  };

  const handleLoadDraft = () => {
    const data = getDataLocalStorage('dataDraft');
    if (!data) return;

    // Switch opensentences types
    dispatch(actions.synthesisRequest.updateOpenSentences(data.openSentences));

    // Load Voice Type
    dispatch(actions.synthesisRequest.updateVoice(data.voice));

    // Load inputLengh
    onChangeInputLength(data.inputLength);
    // Load setting of voice type
    const synthesisRequestSave = data.synthesisRequest;
    Object.keys(synthesisRequestSave).map((key) =>
      dispatch(
        actions.synthesisRequest.updateSynthesisRequest(
          key,
          synthesisRequestSave[key],
        ),
      ),
    );
    // Load content  edit save
    onLoadEditContent(
      data.sentencesSave,
      data.advanceFeature,
      data.openSentences,
      data.enableEditor,
    );
  };

  const getTitle = (plainText, voiceLanguage) => {
    const SPACE = ' ';
    const CHINESE_CHARACTERS_LANGUAGE_CODES = ['cmn-CN', 'ja-JP', 'ko-KR'];

    if (plainText.length <= MAX_LENGTH_TITLE) {
      return plainText;
    }

    const lastSpaceIndex = plainText.lastIndexOf(SPACE, MAX_TITLE_LENGTH - 1);
    const firstSpaceIndex = plainText.indexOf(SPACE);

    if (
      firstSpaceIndex !== -1 &&
      firstSpaceIndex <= MAX_TITLE_LENGTH - 1 &&
      !CHINESE_CHARACTERS_LANGUAGE_CODES.includes(voiceLanguage)
    )
      return plainText.substring(0, lastSpaceIndex + 1);

    return plainText.substring(0, MAX_TITLE_LENGTH);
  };

  const handleClickTitle = () => {
    if (synthesisRequest.title) return;

    let sentencesSave;
    if (advanceFeature) sentencesSave = paragraphs;
    if (!advanceFeature) {
      if (openSentences) {
        sentencesSave = sentences;
      } else {
        sentencesSave = splitParagraphIntoSentence(
          content.getCurrentContent().getPlainText('\n'),
          voice,
        );
      }
    }

    const text = sentencesSave.reduce(
      (acc, sentence) => acc + sentence.text,
      ' ',
    );

    const plainText = text
      .replace(REGEX.ADVANCE_TAG, '')
      .replace(BREAK_TIME_REGEX, ' ')
      .trim();
    if (plainText) {
      const newTitle = getTitle(plainText, voice.languageCode);
      handleChangeTTSTitle({ newTitle });
    }
  };

  useEffect(() => {
    if (inputLength) {
      setIsOpenDialog(false);
      setIsSaving(true);
      setIsShowUp(true);
    }
    debounce(processContentDraft, SET_TIME_AUTO_SAVE)();
  }, [
    advanceFeature,
    openSentences,
    inputLength,
    sentences,
    voice,
    synthesisRequest,
    paragraphs,
  ]);

  useEffect(() => {
    const data = getDataLocalStorage('dataDraft');
    const doneTourGuide = getTTSTourGuideValue(user?.id);
    if (
      data &&
      user.hasAgreedToTerms &&
      doneTourGuide &&
      !isCustomerSupportView
    ) {
      onOpenConfirmDialog();
    }
    setIsOpenDialog(true);

    return () => {
      setIsOpenDialog(true);
    };
  }, [routerPath, user.hasAgreedToTerms]);

  useEffect(() => {
    if (isLoadDraft) handleLoadDraft();
  }, [isLoadDraft]);

  useEffect(() => {
    if (isSaving) {
      setIsShowUp(true);
    } else {
      debounce(handleChangeShowUp, 1000)();
    }
  }, [isSaving]);

  const renderSynthesisRequestDateTime = () => {
    if (synthesisRequest?.createdAt && !enableEditor) {
      return calculateElapsedDateTime(synthesisRequest?.createdAt, t);
    }
    return <div className="empty-title-footer" />;
  };

  const renderMobileTitleBar = () => (
    <StyledTitleBar isNewLayout={isNewLayout} className="mobile-title-bar">
      <div className="base-input">
        <ResizeTextBox
          value={synthesisRequest.title || ''}
          placeholder={t('noTitle')}
          onValidate={handleValidateTTSTitle}
          onChange={handleChangeTTSTitle}
          onClick={handleClickTitle}
          maxWidth={100}
          maxWidthxl={350}
        />

        <StatusSaving savingStatus={isSaving} />
      </div>
      <Box display="flex" gap="5px" alignItems="center">
        <RequestStatus
          request={synthesisRequest}
          screenType={SCREEN_TYPE.MOBILE}
        />
        <ActionButtons
          request={synthesisRequest}
          audioRef={audioRef}
          setCloseMoreMenu={setCloseMoreMenu}
        />
        {enableEditor ? (
          <Button
            id={TTS_GUIDE.CONVERT}
            color="primary"
            variant="contained"
            disabled={!hasConvertTts}
            onClick={handleClickTransfer}
            className="icon-button"
          >
            <CachedRounded />
          </Button>
        ) : (
          <Button
            id={TTS_GUIDE.CONVERT}
            color="primary"
            variant="contained"
            disabled={!hasConvertTts || isClickTransfer}
            onClick={handleOpenMobileCreateButton}
            className="icon-button"
          >
            <AddRounded />
          </Button>
        )}
      </Box>
      <StyledPopover
        open={Boolean(mobileCreateButtonAnchorEl)}
        anchorEl={mobileCreateButtonAnchorEl}
        onClose={handleCloseMobileCreateButton}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: -10,
          horizontal: 'right',
        }}
      >
        <StyledCreateNewEditor onClick={() => handleCreateNewEditor(true)}>
          <Typography className="text-action">{t('createNew')}</Typography>
        </StyledCreateNewEditor>
        <StyledCreateNewEditor onClick={() => handleCreateNewEditor(false)}>
          <Typography className="text-action">
            {t('copyAndCreateNew')}
          </Typography>
        </StyledCreateNewEditor>
      </StyledPopover>
    </StyledTitleBar>
  );

  const audioExpired = moment().isAfter(
    moment(synthesisRequest?.createdAt).add(
      synthesisRequest?.retentionPeriod,
      'days',
    ),
  );

  const loadingUXOld = !hasConvertTts || isClickTransfer;
  // ============= ******Function New UX systhis******* ===============
  const dataRequestIsChanged = useMemo(() => {
    const baseDataSynthesis = {
      title: synthesisRequest?.title || '',
      audioType: synthesisRequest.audioType,
      clientPause: synthesisRequest.clientPause || {},
      bitrate: synthesisRequest.bitrate,
      pronunciationDict: synthesisRequest.pronunciationDict || [],
      openSentences,
    };
    const DEFAULT_SPPED = 1;
    const { backgroundMusic, ...other } =
      getDataLocalStorage('relatedRequest') || {};
    if (!openSentences) {
      baseDataSynthesis.voice = voice?.name;
      baseDataSynthesis.text = advanceFeature
        ? convertParagraphsToText(paragraphs)
        : content.getCurrentContent().getPlainText('\n');
      baseDataSynthesis.speed = synthesisRequest.speed || DEFAULT_SPPED;
    }
    if (synthesisRequest.backgroundMusic?.link) {
      const { link, name, volume } = synthesisRequest.backgroundMusic ?? {};
      baseDataSynthesis.backgroundMusic = { link, name, volume };
    }

    if (openSentences && sentences) {
      baseDataSynthesis.sentences = sentences.map((sentence) => ({
        breakTime: sentence?.breakTime,
        characters: sentence?.text?.length,
        speed: sentence?.speed,
        text: sentence?.text,
        voice_code: sentence?.voice?.code,
      }));
    }
    const hashBackgroundMusic = backgroundMusic?.link;
    const selectBackgroundMusic = {
      link: backgroundMusic?.link,
      name: backgroundMusic?.name,
      volume: backgroundMusic?.volume,
    };
    const relatedRequestOldSession = {
      ...other,
      ...(hashBackgroundMusic
        ? { backgroundMusic: selectBackgroundMusic }
        : {}),
    };
    const dataRelated = isEmpty(relateDataRequest)
      ? relatedRequestOldSession
      : relateDataRequest;
    const isDataChanged = !isEqual(dataRelated, baseDataSynthesis);
    if (isDataChanged) {
      setIsOpenDialog(true);
    }
    return isEqual(dataRelated, baseDataSynthesis);
  }, [
    relateDataRequest,
    voice,
    sentences,
    paragraphs,
    synthesisRequest,
    openSentences,
    content,
  ]);
  const { displayStatus } = useRequestAnimationStatus(synthesisRequest);
  const isAllowTTS = useMemo(
    () => audioExpired || synthesisRequest?.status === REQUEST_STATUS.FAILURE,
    [audioExpired, dataRequestIsChanged, synthesisRequest],
  );

  const requestProcessing = useMemo(() => {
    // The editor content no longer matches the submitted request, so the bar
    // is offering a new conversion rather than reporting on the old one.
    if (!dataRequestIsChanged) return false;
    if (groupLoading) return true;
    // Nothing has been submitted from this editor, such as the default sample
    // script or a fresh editor, so there is nothing to be processing.
    if (!synthesisRequest?.id) return false;
    // useRequestAnimationStatus leaves displayStatus undefined until the
    // request carries a processingAt, so "not IN_PROGRESS" must never be read
    // as "finished". Only a real outcome releases the processing state.
    if (!isTerminalRequestStatus(synthesisRequest?.status)) return true;
    // After the backend finishes, the progress animation deliberately keeps
    // reporting IN_PROGRESS for two more seconds. Stay in step with it so the
    // buttons appear at the same moment the bar below reaches the end.
    return displayStatus === REQUEST_STATUS.IN_PROGRESS;
  }, [
    dataRequestIsChanged,
    groupLoading,
    displayStatus,
    synthesisRequest?.id,
    synthesisRequest?.status,
  ]);

  const showPreviewBtnNewUxSynthesis = useMemo(
    () => dataRequestIsChanged || isDefault,
    [isDefault, dataRequestIsChanged],
  );
  const showEtimateAudioLength = useMemo(() => {
    const shouldShowPreviewButton =
      showPreviewBtnNewUxSynthesis &&
      estimateAudioLength &&
      !synthesisRequest?.status;
    const shouldShowEstimateIfDataNotChanged =
      !dataRequestIsChanged && estimateAudioLength;

    return shouldShowPreviewButton || shouldShowEstimateIfDataNotChanged;
  }, [
    synthesisRequest,
    dataRequestIsChanged,
    isOpenDialog,
    estimateAudioLength,
    displayStatus,
    showPreviewBtnNewUxSynthesis,
  ]);

  useMemo(async () => {
    if (loadingUXOld) {
      setGroupLoading(true);
    }
    if (groupLoading && !loadingUXOld) {
      await delay(1000);
      setGroupLoading(false);
    }
  }, [loadingUXOld]);

  return (
    <>
      <StyledTitleBar
        isNewLayout={isNewLayout}
        useNewUXSynthesis
        className="web-title-bar"
        ref={titleRef}
        disabledBtn={requestProcessing}
      >
        {!isNewLayout && (
          <div className="base-input">
            <ResizeTextBox
              value={synthesisRequest.title || ''}
              placeholder={t('noTitle')}
              onValidate={handleValidateTTSTitle}
              onClick={handleClickTitle}
              onChange={handleChangeTTSTitle}
              maxWidth={
                isShowUp
                  ? titleWidth - requestInfoWidth - SAVE_TITLE_WIDTH
                  : titleWidth - requestInfoWidth - TITLE_MARGIN_RIGHT
              }
              maxWidthxl={350}
              footerLabel={renderSynthesisRequestDateTime()}
            />
            {isShowUp && !isNewLayout && !synthesisRequest?.status && (
              <StatusSaving savingStatus={isSaving} />
            )}
          </div>
        )}
        <div className="request-info" ref={requestInfoRef}>
          {!!isNewLayout && (
            <>
              {!isExpiredPackage && (
                <Tooltip arrow title={t('charactersInUse')} placement="top">
                  <Typography variant="body2" className="character-default">
                    <span
                      className={classNames('', {
                        'character-red': characterExceed,
                        'character-in-use': !dataRequestIsChanged,
                      })}
                      translate="no"
                    >
                      {inputLength?.toLocaleString(language) || 0}
                    </span>
                    {dataRequestIsChanged ? (
                      <span className="style-character" translate="no">
                        {' '}
                        {t('characters')}
                      </span>
                    ) : (
                      <span translate="no">
                        {`/${totalCharacters?.toLocaleString(language) || 0}`}
                      </span>
                    )}
                  </Typography>
                </Tooltip>
              )}
              {showEtimateAudioLength ? (
                <Tooltip
                  arrow
                  title={t('estimatedAudioLength')}
                  placement="top"
                >
                  <Typography variant="body2" className="estimate-audio-length">
                    <TimerOutlined sx={{ fontSize: 16 }} color="iconPrimary" />
                    <span translate="no"> {estimateAudioLength}</span>
                  </Typography>
                </Tooltip>
              ) : (
                <div style={{ marginRight: 10 }} />
              )}
              {dataRequestIsChanged && (
                <RequestStatus request={synthesisRequest} marginLeft="8px" />
              )}
              {dataRequestIsChanged && (
                <>
                  <ActionButtonsNew
                    request={synthesisRequest}
                    audioRef={audioRef}
                    useNewUXSynthesis
                    setCloseMoreMenu={setCloseMoreMenu}
                  />
                </>
              )}
              {showPreviewBtnNewUxSynthesis &&
              !requestProcessing &&
              !isAllowTTS ? (
                <StyledButtonPlay>
                  <PlayAudioButton
                    dataAttribute={
                      isDefault ? 'play-default-audio' : 'play-curr-audio'
                    }
                    onPlayPreviewAudio={isDefault && handleCheckIsSampleScript}
                    audioRef={audioRef}
                    request={synthesisRequest}
                    langEng={language !== LANGUAGE.VN}
                    hasDisable={!synthesisRequest?.audioLink}
                    getTitleActionTooltip={() => ''}
                  />
                </StyledButtonPlay>
              ) : (
                <Tooltip
                  arrow
                  placement="top"
                  open={
                    enableCreditUsagePreview &&
                    !requestProcessing &&
                    openTooltipCostsCredit
                  }
                  disableHoverListener={
                    !enableCreditUsagePreview || requestProcessing
                  }
                  title={t('costsCredit', {
                    value: (
                      voice?.creditFactor * inputLength || 0
                    ).toLocaleString(language),
                  })}
                  onMouseEnter={() => setOpenTooltipCostsCredit(true)}
                  onMouseLeave={() => setOpenTooltipCostsCredit(false)}
                >
                  <Button
                    id={TTS_GUIDE.CONVERT}
                    color="primary"
                    variant="contained"
                    data-id="generate-audio"
                    disabled={requestProcessing || loadingUXOld}
                    onClick={handleClickTransfer}
                    className={classNames('text-transfer-button', {
                      'opacity-loading': requestProcessing || loadingUXOld,
                    })}
                  >
                    <ProcessHandler
                      loading={loading}
                      ml="46px"
                      mr="46px"
                      size={24}
                      align="center"
                      color="divider"
                    >
                      {requestProcessing || loadingUXOld ? (
                        <div className="request-status">
                          <CircularProgress
                            value={75}
                            className="slow-load-request"
                            size="24px"
                          />
                          <div className="text-button-request">
                            {t('process')}
                          </div>
                        </div>
                      ) : (
                        <>
                          {(!dataRequestIsChanged || isAllowTTS) && (
                            <div className="request-status">
                              <SlowMotionVideoRoundedIcon
                                value={75}
                                className="slow-load-request"
                                size="24px"
                              />
                              <div className="text-button-request">
                                {t('createAudio')}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </ProcessHandler>
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </StyledTitleBar>
      {!isNewLayout && renderMobileTitleBar()}
    </>
  );
};

export default TitleBar;
